import React, { type FC, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { WFA_DASHBOARD_BUSINESS_UNITS_CONTAINER_DAID } from 'data-automation-ids/WfaDashboardAutomationIds';
import { BusinessUnitLoadingStates } from 'src/apps/dashboard/components/business-unit-loading-states/BusinessUnitLoadingStates';
import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useBusinessUnitRedirect } from 'src/apps/dashboard/hooks/useBusinessUnitRedirect';
import { useBusinessUnitTabs } from 'src/apps/dashboard/hooks/useBusinessUnitTabs';
import { useConfigurationFilters } from 'src/apps/dashboard/hooks/useConfigurationFilters';
import { useDashboardDataQuery } from 'src/apps/dashboard/hooks/useDashboardDataQuery';
import { useScoreDataQuery } from 'src/apps/dashboard/hooks/useScoreDataQuery';
import { useBusinessUnitsFromConfig } from 'src/apps/dashboard/stores/dashboardConfigStore';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { DashboardRouteParams } from 'src/apps/dashboard/types/DashboardTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { useFiltersUrlSync } from 'src/common/hooks/useFiltersUrlSync';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { HEADER, WEB_RESET_TABLE_IDS } from './DashboardBusinessUnits.constants';

export const DashboardBusinessUnits: FC = () => {
	usePlatformConfigInitializer();
	const { unitId } = useParams<DashboardRouteParams>();
	const history = useHistory();
	const { businessUnits, isLoading: isConfigLoading } = useBusinessUnitsFromConfig();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();
	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();
	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	const { filtersFromUrl, handleFilterChange: syncUrl } = useFiltersUrlSync({
		filterDefinitions,
		filterOptions
	});
	const effectiveFilters = useEffectiveFilters(filtersFromUrl, filterDefinitions);
	const { onTablePageOrPageSizeChange, getTableStateById } = useTableState();

	const handleFilterChange = useCallback(
		(newFilters: IFilterOptions) => {
			const currentDateRange = filtersFromUrl?.dateRange?.[0]?.value;
			const newDateRange = newFilters?.dateRange?.[0]?.value;

			if (currentDateRange !== newDateRange) {
				WEB_RESET_TABLE_IDS.forEach((tableId) => {
					const state = getTableStateById(tableId);
					onTablePageOrPageSizeChange(tableId, {
						...state.paginationInfo,
						page: 1
					});
				});
			}

			syncUrl(newFilters);
		},
		[filtersFromUrl, syncUrl, getTableStateById, onTablePageOrPageSizeChange]
	);

	const businessUnitName =
		businessUnits.find((bu) => bu.id === unitId)?.name ?? unitId ?? 'Unknown';

	const { setActiveTabIndex, tabs, tabPanels } = useBusinessUnitTabs({
		businessUnitName,
		effectiveFilters
	});

	useBusinessUnitRedirect({ unitId, businessUnits, history });

	const filtersWithBusinessUnit = useMemo(() => {
		if (!unitId) {
			return effectiveFilters;
		}
		return {
			...effectiveFilters,
			unitId: [{ value: unitId, label: businessUnitName }]
		};
	}, [effectiveFilters, unitId, businessUnitName]);

	const dashboardDataQuery = useDashboardDataQuery(
		filtersWithBusinessUnit,
		labelsExcludedFromDashboard,
		filterDefinitions
	);
	const scoreDataQuery = useScoreDataQuery(
		filtersWithBusinessUnit,
		labelsExcludedFromDashboard,
		filterDefinitions
	);

	const filter = useMemo(
		() => ({
			filters: filtersFromUrl,
			filterDefinitions,
			filterOptions,
			onFilterChange: handleFilterChange
		}),
		[filtersFromUrl, filterDefinitions, filterOptions, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data,
			isLoading: dashboardDataQuery.isLoading,
			error: dashboardDataQuery.error
		}),
		[dashboardDataQuery.data, dashboardDataQuery.isLoading, dashboardDataQuery.error]
	);

	const bottom = useMemo(
		() => ({
			kind: 'tabs' as const,
			sectionVariant: 'businessUnitSubTabs' as const,
			title: `Tests labeled with this ${businessUnitDisplayName || 'business unit'}`,
			tabState: {
				tabs,
				tabPanels,
				activeTabIndex: 0,
				onTabSelected: setActiveTabIndex,
				tabsId: 'business-unit-tabs',
				tabsAriaLabel: 'Business unit sub-tabs',
				tabsDataAutomationId: 'business-unit-tabs'
			}
		}),
		[businessUnitDisplayName, tabs, tabPanels, setActiveTabIndex]
	);

	if (isConfigLoading || (!isConfigLoading && businessUnits.length === 0) || !unitId) {
		return (
			<BusinessUnitLoadingStates
				isConfigLoading={isConfigLoading}
				businessUnits={businessUnits}
				unitId={unitId}
			/>
		);
	}

	return (
		<DashboardTemplate
			containerAutomationId={WFA_DASHBOARD_BUSINESS_UNITS_CONTAINER_DAID}
			header={HEADER}
			filter={filter}
			effectiveFilters={filtersWithBusinessUnit}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			chartVariant="stacked"
			bottom={bottom}
		/>
	);
};
